import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { UpgradeModal } from '../BillingModal';
import { useAuth } from '../../context/AuthContext';
import {
  createPhaseBuildEventDetail,
  getPhaseBuildBusyLabel,
  getPhaseBuildLabel,
  getPhaseBuildTitle,
  isPhaseBuildActionEnabled,
  isPhaseResolutionRequired,
  usePhaseBuildState,
} from '../../hooks/usePhaseBuildState';
import { errorMessage, normalizeApiError } from '../../services/apiError';
import { projectService } from '../../services/projectService';
import type { BuildScope, ProjectAgentPreference } from '../../services/projectService';
import { signalInsufficientCredits } from '../../services/creditGateEvents';
import { Task } from '../../services/taskService';
import { useGetTasksQuery } from '../../store/services/tasksApi';
import { trackBuildStart } from '../../utils/analytics';
import { calculateTaskCredit } from '../../utils/agentWeights';
import { PhaseBuildStatus } from '../PhaseBuildStatus';
import { useToast } from '../Toast/Toast';

import { MODEL_MULTIPLIERS, ModelTier } from './ChatBoxInput';
import type { BuildFlowResolvedDetail } from './buildFlowResolution';
import './StartBuildingButton.css';

interface StartBuildingButtonProps {
  projectId: string | null;
  isAnyAgentRunning: boolean;
  selectedModel: ModelTier;
  hasByokKeys?: boolean;
  onOpenSettings?: () => void;
}

interface ScopedStartBuildingButtonProps {
  projectId: string | null;
  scope: BuildScope;
  taskCount: number;
  forceExecutionTaskIds?: string[];
  disabled?: boolean;
  label?: string;
  className?: string;
  title?: string;
  selectedModel?: ModelTier;
  hasByokKeys?: boolean;
  onOpenSettings?: () => void;
  onStarted?: () => void;
}

type BuildResult = {
  error_code?: string;
  error_message?: string;
  required_credits?: number;
  available_credits?: number;
  credit_shortfall?: number;
  phase_id?: string;
  phase_number?: number;
  phase_name?: string;
  warning_message?: string;
  backlogged_tasks?: number;
};

const getTaskCreditCost = (
  task: Task,
  selectedModel: ModelTier,
  projectAgents?: Record<string, ProjectAgentPreference>
) => (
  calculateTaskCredit(task, MODEL_MULTIPLIERS, selectedModel, projectAgents)
);

/** Walk candidate tasks in order, summing model-adjusted prompt_credit until budget exhausted. */
const computeAffordableCount = (
  candidateTasks: Task[],
  budget: number,
  selectedModel: ModelTier,
  projectAgents?: Record<string, ProjectAgentPreference>
) => {
  let remaining = budget;

  for (let i = 0; i < candidateTasks.length; i++) {
    const cost = getTaskCreditCost(candidateTasks[i], selectedModel, projectAgents);

    if (remaining < cost) return i;
    remaining -= cost;
  }

  return candidateTasks.length;
};

const sortedBuildCandidates = (tasks: Task[]): Task[] => {
  const todoTasks = tasks.filter((task) => task.status === 'todo');
  const candidates = todoTasks.length > 0
    ? todoTasks
    : tasks.filter((task) => task.status === 'backlog' && task.can_start);

  return candidates.sort((a, b) => (
    (a.dependency_depth - b.dependency_depth) || (a.position - b.position)
  ));
};

const computeProgress = (
  tasks: Task[],
  creditBalance: number,
  selectedModel: ModelTier,
  projectAgents?: Record<string, ProjectAgentPreference>
) => {
  if (!tasks?.length) return null;

  const candidateTasks = sortedBuildCandidates(tasks);
  const todoCount = candidateTasks.length;
  const completed = tasks.filter((t) => t.status === 'done').length;
  const affordableCount = computeAffordableCount(
    candidateTasks,
    creditBalance,
    selectedModel,
    projectAgents,
  );

  return {
    completed,
    total: tasks.length,
    todoCount,
    affordableCount,
    isLimited: affordableCount < todoCount,
    todoTaskObjects: candidateTasks,
  };
};

export const useBuildProgress = (projectId: string | null, selectedModel: ModelTier = 'super_large') => {
  const { currentData: tasks = [] } = useGetTasksQuery(projectId ?? '', {
    skip: !projectId,
  });
  const { user } = useAuth();
  const [projectAgents, setProjectAgents] = useState<Record<string, ProjectAgentPreference>>({});

  useEffect(() => {
    if (!projectId) {
      setProjectAgents({});
      return undefined;
    }

    let cancelled = false;
    const loadProjectAgents = async () => {
      try {
        const response = await projectService.getProjectAgents(projectId);
        if (!cancelled) {
          setProjectAgents(response.agents);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load project agents for build progress:', error);
        }
      }
    };

    const handleAgentUpdate = (event: Event) => {
      const detail = (event as CustomEvent<{ projectId?: string; isTaskContext?: boolean }>).detail;
      if (!detail || detail.projectId === projectId) {
        void loadProjectAgents();
      }
    };

    void loadProjectAgents();
    window.addEventListener('agents_updated', handleAgentUpdate);

    return () => {
      cancelled = true;
      window.removeEventListener('agents_updated', handleAgentUpdate);
    };
  }, [projectId]);

  return useMemo(
    () => computeProgress(tasks.filter((task) => task.task_type !== 'planning') as Task[], user?.credits_balance ?? 0, selectedModel, projectAgents),
    [tasks, user?.credits_balance, selectedModel, projectAgents],
  );
};

const handleBuildResult = (
  result: BuildResult,
  toast: { error: (msg: string) => void; warning: (msg: string) => void }
): void => {
  if (result.error_code === 'INSUFFICIENT_CREDITS') {
    signalInsufficientCredits(
      result.credit_shortfall
      ?? Math.max(0, (result.required_credits ?? 0) - (result.available_credits ?? 0)),
    );
  } else if (result.error_code) {
    toast.error(result.error_message || 'The build could not be started. Review the project tasks and try again.');
  }

  if (!result.error_code && result.warning_message) {
    toast.warning(result.warning_message);
  }
};

const getForceExecutionTaskIds = (
  progress: ReturnType<typeof useBuildProgress>,
): string[] => {
  return (progress?.todoTaskObjects ?? [])
    .filter((task) => task.force_execution)
    .map((task) => task.id);
};

const handleBuildError = (error: unknown, toast: { error: (msg: string) => void }): void => {
  const normalized = normalizeApiError(error);

  if (normalized.status === 402 || normalized.code === 'INSUFFICIENT_CREDITS') {
    signalInsufficientCredits();
  } else {
    toast.error(errorMessage(error, 'The build could not be started. Review the project tasks and try again.'));
  }
};

const executeBuild = async (
  projectId: string,
  toast: { error: (msg: string) => void; warning: (msg: string) => void },
  selectedModel: ModelTier = 'super_large',
  forceExecutionTaskIds: string[] = [],
  scope: BuildScope = {},
): Promise<boolean> => {
  try {
    const result = await projectService.triggerBuild(projectId, selectedModel, forceExecutionTaskIds, scope);

    handleBuildResult(result, toast);

    return !result.error_code;
  } catch (error) {
    handleBuildError(error, toast);

    return false;
  }
};

/** Core build pipeline: trigger backend-selected executable tasks. */
const runBuildPipeline = async (
  projectId: string,
  progress: ReturnType<typeof useBuildProgress>,
  toast: { error: (msg: string) => void; warning: (msg: string) => void },
  selectedModel: ModelTier,
  phaseId?: string | null,
  selectedPhaseTaskIds?: string[],
  selectionSummaryId?: string,
): Promise<boolean> => {
  const forceExecutionTaskIds = getForceExecutionTaskIds(progress);

  return executeBuild(
    projectId,
    toast,
    selectedModel,
    forceExecutionTaskIds,
    {
      phaseId: phaseId ?? null,
      selectedPhaseTaskIds,
      selectionSummaryId,
    },
  );
};

/** Returns true if a BYOK build is missing the required provider key. */
export const checkTierGate = (
  user: { subscription_tier?: string } | null,
  setShowUpgradeModal: (v: boolean) => void,
  hasByokKeys?: boolean,
  onOpenSettings?: () => void,
): boolean => {
  const tier = (user?.subscription_tier || 'free').toLowerCase();

  if (tier === 'byok' && !hasByokKeys) return (onOpenSettings?.(), true);

  return false;
};

/** Execute the full build flow (credit-gate → pipeline). */
const startBuildFlow = async (
  projectId: string | null,
  progress: ReturnType<typeof useBuildProgress>,
  toast: { error: (msg: string) => void; warning: (msg: string) => void },
  selectedModel: ModelTier,
  setIsBuilding: (v: boolean) => void,
  skipNavigation = false,
  phaseId?: string | null,
  selectedPhaseTaskIds?: string[],
  selectionSummaryId?: string,
): Promise<boolean> => {
  if (!projectId) return false;

  setIsBuilding(true);
  trackBuildStart();
  if (!skipNavigation) {
    window.dispatchEvent(new Event('switch_to_tasks_tab'));
  }

  const started = await runBuildPipeline(
    projectId,
    progress,
    toast,
    selectedModel,
    phaseId,
    selectedPhaseTaskIds,
    selectionSummaryId,
  );

  if (!started) setIsBuilding(false);

  return started;
};

/** Lock-then-build helper (not a hook, no statement-count issue). */
const executeLocked = async (
  lockRef: React.MutableRefObject<boolean>,
  setIsBuilding: (v: boolean) => void,
  projectId: string | null,
  progress: ReturnType<typeof useBuildProgress>,
  toast: { error: (msg: string) => void; warning: (msg: string) => void },
  selectedModel: ModelTier,
  skipNavigation = false,
  phaseId?: string | null,
  selectedPhaseTaskIds?: string[],
  selectionSummaryId?: string,
): Promise<boolean> => {
  lockRef.current = true; setIsBuilding(true);

  return startBuildFlow(projectId, progress, toast, selectedModel, (v) => {
    setIsBuilding(v); if (!v) lockRef.current = false;
  }, skipNavigation, phaseId, selectedPhaseTaskIds, selectionSummaryId);
};

/** Unique id for one build-button click; threads trigger_start_build →
 *  build_flow_resolved so only the clicking button reacts to its resolution. */
export const newBuildClickId = (): string => (
  `build-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

/** Store a fresh click id in the ref and dispatch trigger_start_build carrying it. */
export const dispatchStartBuild = (
  clickIdRef: React.MutableRefObject<string | null>,
  phaseId?: string | null,
  selectedPhaseTaskIds?: string[],
  selectionSummaryId?: string,
): void => {
  clickIdRef.current = newBuildClickId();
  window.dispatchEvent(new globalThis.CustomEvent('trigger_start_build', {
    detail: createPhaseBuildEventDetail(
      clickIdRef.current,
      phaseId,
      selectedPhaseTaskIds,
      selectionSummaryId,
    ),
  }));
};

/** Resolve a build click. Deferred a tick so listeners that subscribe during
 *  the same click's render pass still receive the resolution. */
export const dispatchBuildFlowResolved = (ok: boolean, clickId?: string | null, reason?: string): void => {
  window.setTimeout(() => {
    window.dispatchEvent(new globalThis.CustomEvent('build_flow_resolved', {
      detail: { ok, clickId: clickId ?? null, reason },
    }));
  }, 0);
};

/** Subscribe to build_flow_resolved. Plain (unscoped) events reset every
 *  listener — legacy dispatchers — while clickId-scoped events only reset
 *  the button whose ref generated that clickId. */
export const useBuildResolvedListener = (
  clickIdRef: React.MutableRefObject<string | null>,
  setLoading: (v: boolean) => void,
  onResolved?: (detail: BuildFlowResolvedDetail) => void,
) => {
  useEffect(() => {
    const handleResolved = (event: Event) => {
      const detail = (
        event as globalThis.CustomEvent<BuildFlowResolvedDetail>
      ).detail ?? {};

      if (detail?.clickId && detail.clickId !== clickIdRef.current) return;
      setLoading(false);
      onResolved?.(detail);
    };

    window.addEventListener('build_flow_resolved', handleResolved);

    return () => window.removeEventListener('build_flow_resolved', handleResolved);
  }, [clickIdRef, onResolved, setLoading]);
};

export const useStartBuildListener = (
  handleBuild: (
    skipNavigation?: boolean,
    clickId?: string,
    phaseId?: string | null,
    selectedPhaseTaskIds?: string[],
    selectionSummaryId?: string,
  ) => void,
) => {
  useEffect(() => {
    const onTrigger = (event: Event) => {
      const ce = event as globalThis.CustomEvent<{
        skipNavigation?: boolean;
        clickId?: string;
        phaseId?: string | null;
        selectedPhaseTaskIds?: string[];
        selectionSummaryId?: string;
      }>;
      handleBuild(
        ce.detail?.skipNavigation,
        ce.detail?.clickId,
        ce.detail?.phaseId,
        ce.detail?.selectedPhaseTaskIds,
        ce.detail?.selectionSummaryId,
      );
    };

    window.addEventListener('trigger_start_build', onTrigger);

    return () => window.removeEventListener('trigger_start_build', onTrigger);
  }, [handleBuild]);
};

export const useBuildTrigger = (
  projectId: string | null,
  selectedModel: ModelTier,
  progress: ReturnType<typeof useBuildProgress>,
  hasByokKeys?: boolean,
  onOpenSettings?: () => void,
) => {
  const [isBuilding, setIsBuilding] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const lockRef = useRef(false); const toast = useToast(); const { user } = useAuth();
  const phaseBuild = usePhaseBuildState(projectId, selectedModel);
  const {
    state: phaseState,
    resolveCurrentPhase,
  } = phaseBuild;

  // Every exit path must resolve the flow, echoing the triggering clickId,
  // or the dispatching button is left spinning with no resolution event.
  const handleBuild = useCallback(async (
    skipNavigation = false,
    clickId?: string,
    requestedPhaseId?: string | null,
    selectedPhaseTaskIds?: string[],
    selectionSummaryId?: string,
  ) => {
    if (lockRef.current) {
      dispatchBuildFlowResolved(false, clickId, 'in_flight');

      return;
    }
    if (!projectId) {
      dispatchBuildFlowResolved(false, clickId, 'no_project');

      return;
    }
    const hasExplicitPhaseSelection = selectedPhaseTaskIds !== undefined;
    const hasSelectedPhaseTasks = Boolean(selectedPhaseTaskIds?.length);

    if (hasExplicitPhaseSelection && !hasSelectedPhaseTasks) {
      toast.error('Select at least one task to build.');
      dispatchBuildFlowResolved(false, clickId, 'no_tasks_selected');

      return;
    }

    if (phaseState?.current_phase && isPhaseResolutionRequired(phaseState) && !hasExplicitPhaseSelection) {
      setIsBuilding(true);
      try {
        await resolveCurrentPhase();
        dispatchBuildFlowResolved(true, clickId, 'phase_tasks_opened');
      } catch {
        toast.error('Could not resolve the phase task order.');
        dispatchBuildFlowResolved(false, clickId, 'phase_resolution_failed');
      } finally {
        setIsBuilding(false);
      }

      return;
    }
    if (checkTierGate(user, setShowUpgradeModal, hasByokKeys, onOpenSettings)) {
      dispatchBuildFlowResolved(false, clickId, 'tier_gate');

      return;
    }
    const isLegacyProject = phaseState?.blocking_reason === 'assignment_required';

    if (!isLegacyProject && (!phaseState?.current_phase || (!phaseState.can_start && !hasSelectedPhaseTasks))) {
      toast.error(getPhaseBuildTitle(phaseState));
      dispatchBuildFlowResolved(false, clickId, phaseState?.blocking_reason || 'phase_not_ready');

      return;
    }
    const started = await executeLocked(
      lockRef,
      setIsBuilding,
      projectId,
      progress,
      toast,
      selectedModel,
      skipNavigation,
      isLegacyProject ? null : requestedPhaseId || phaseState.current_phase?.id,
      isLegacyProject ? undefined : selectedPhaseTaskIds,
      isLegacyProject ? undefined : selectionSummaryId,
    );
    lockRef.current = false;
    dispatchBuildFlowResolved(started, clickId, started ? undefined : 'build_failed');
  }, [
    projectId,
    toast,
    selectedModel,
    progress,
    user,
    hasByokKeys,
    onOpenSettings,
    phaseState,
    resolveCurrentPhase,
  ]);

  const onClickBuild = useCallback(() => {
    void handleBuild(false);
  }, [handleBuild]);

  return {
    isBuilding,
    handleBuild,
    onClickBuild,
    showUpgradeModal,
    setShowUpgradeModal,
    phaseBuild,
  };
};

const BuildIcon: React.FC = () => (
  <svg className="build-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
    <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
    <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 3 0 3 0" />
    <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-3 0-3" />
  </svg>
);

export function ScopedStartBuildingButton({
  projectId,
  scope,
  taskCount,
  forceExecutionTaskIds = [],
  disabled = false,
  label,
  className = '',
  title,
  selectedModel = 'super_large',
  hasByokKeys,
  onOpenSettings,
  onStarted,
}: ScopedStartBuildingButtonProps): React.ReactElement {
  const [isBuilding, setIsBuilding] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const lockRef = useRef(false);
  const toast = useToast();
  const { user } = useAuth();
  const phaseBuild = usePhaseBuildState(projectId, selectedModel);
  const {
    state: phaseState,
    isLoading: isPhaseLoading,
    refresh: refreshPhaseState,
    resolveCurrentPhase,
  } = phaseBuild;
  const currentPhase = phaseState?.current_phase;
  const isTaskSelectionBuild = !scope.sprintId && !scope.phaseId;
  const hasSelectedBuildTasks = Boolean(scope.taskIds?.length);
  const hasExplicitPhaseSelection = scope.selectedPhaseTaskIds !== undefined;
  const hasSelectedPhaseTasks = Boolean(scope.selectedPhaseTaskIds?.length);
  const scopeLocked = Boolean(
    scope.sprintId
    || (scope.phaseId && scope.phaseId !== currentPhase?.id),
  );
  const buttonLabel = label || getPhaseBuildLabel(phaseState, isPhaseLoading);
  const buttonTitle = title || getPhaseBuildTitle(phaseState);
  const canResolve = !isTaskSelectionBuild
    && isPhaseResolutionRequired(phaseState)
    && !hasSelectedPhaseTasks;

  const handleScopedBuild = useCallback(async () => {
    if (!projectId || lockRef.current) return;
    if (canResolve) {
      setIsBuilding(true);
      try {
        await resolveCurrentPhase();
        toast.info(`Opened Phase ${currentPhase?.phase_number} tasks`);
      } catch {
        toast.error('Could not resolve the phase task order.');
      } finally {
        setIsBuilding(false);
      }

      return;
    }
    if (checkTierGate(user, setShowUpgradeModal, hasByokKeys, onOpenSettings)) return;
    if (isTaskSelectionBuild && !hasSelectedBuildTasks) {
      toast.error('No dependency-ready To Do or Backlog tasks are available to build.');

      return;
    }
    if (hasExplicitPhaseSelection && !hasSelectedPhaseTasks) {
      toast.error('Select at least one task to build.');

      return;
    }
    if (
      !isTaskSelectionBuild
      && (!currentPhase || (!phaseState?.can_start && !hasSelectedPhaseTasks) || scopeLocked)
    ) {
      toast.error(scopeLocked ? 'Select the current build phase to start it.' : buttonTitle);

      return;
    }

    lockRef.current = true;
    setIsBuilding(true);
    trackBuildStart();

    try {
      const success = await executeBuild(
        projectId,
        toast,
        selectedModel,
        forceExecutionTaskIds,
        isTaskSelectionBuild
          ? scope
          : {
            phaseId: currentPhase?.id,
            selectedPhaseTaskIds: scope.selectedPhaseTaskIds,
          },
      );

      if (success) {
        toast.success(
          isTaskSelectionBuild
            ? `${taskCount} task${taskCount === 1 ? '' : 's'} queued`
            : `Phase ${currentPhase?.phase_number} queued`,
        );
        onStarted?.();
        void refreshPhaseState(true);
      }
    } finally {
      setIsBuilding(false);
      lockRef.current = false;
    }
  }, [
    projectId,
    user,
    hasByokKeys,
    onOpenSettings,
    toast,
    selectedModel,
    forceExecutionTaskIds,
    onStarted,
    phaseState,
    resolveCurrentPhase,
    refreshPhaseState,
    currentPhase,
    canResolve,
    scopeLocked,
    buttonTitle,
    hasSelectedBuildTasks,
    hasExplicitPhaseSelection,
    hasSelectedPhaseTasks,
    isTaskSelectionBuild,
    scope,
    taskCount,
  ]);

  return (
    <div className="start-building-wrapper scoped-start-building-wrapper">
      <button
        type="button"
        onClick={handleScopedBuild}
        disabled={
          disabled
          || isBuilding
          || (
            isTaskSelectionBuild
              ? !hasSelectedBuildTasks
              : (
                (hasExplicitPhaseSelection && !hasSelectedPhaseTasks)
                ||
                isPhaseLoading
                || !isPhaseBuildActionEnabled(phaseState)
                || (scopeLocked && !canResolve)
              )
          )
        }
        title={buttonTitle}
        data-task-count={taskCount}
        className={`btn-start-building btn-start-building--scoped ${className}`.trim()}
      >
        <i className={`fa-solid ${isBuilding ? 'fa-spinner fa-spin' : 'fa-wrench'}`} aria-hidden="true" />
        <span>
          {isBuilding
            ? (isTaskSelectionBuild ? 'Starting…' : getPhaseBuildBusyLabel(phaseState))
            : buttonLabel}
        </span>
      </button>

      {showUpgradeModal && (
        <UpgradeModal onClose={() => setShowUpgradeModal(false)} />
      )}
    </div>
  );
}

/* ── Main Component ── */

export function StartBuildingButton({
  projectId, isAnyAgentRunning, selectedModel, hasByokKeys, onOpenSettings,
}: StartBuildingButtonProps): React.ReactElement | null {
  const progress = useBuildProgress(projectId, selectedModel);
  const {
    isBuilding,
    handleBuild,
    onClickBuild,
    showUpgradeModal,
    setShowUpgradeModal,
    phaseBuild,
  } = useBuildTrigger(projectId, selectedModel, progress, hasByokKeys, onOpenSettings);

  useStartBuildListener(handleBuild);

  const phase = phaseBuild.state?.current_phase;
  const totalCount = phase?.task_count ?? 0;
  const buttonLabel = getPhaseBuildLabel(phaseBuild.state, phaseBuild.isLoading);

  return (
    <div className="start-building-wrapper">
      <PhaseBuildStatus state={phaseBuild.state} compact />
      <button
        onClick={onClickBuild}
        disabled={
          isBuilding
          || isAnyAgentRunning
          || phaseBuild.isLoading
          || !isPhaseBuildActionEnabled(phaseBuild.state)
        }
        className="btn-start-building"
        title={getPhaseBuildTitle(phaseBuild.state)}
      >
        <BuildIcon />
        {isBuilding ? getPhaseBuildBusyLabel(phaseBuild.state) : (
          <>
            {buttonLabel}
            {totalCount > 0 && (
              <span className="build-task-count">
                {totalCount} Task{totalCount !== 1 ? 's' : ''}
              </span>
            )}
          </>
        )}
      </button>

      {showUpgradeModal && (
        <UpgradeModal onClose={() => setShowUpgradeModal(false)} />
      )}
    </div>
  );
}
